import React, { useEffect, useState, createRef } from 'react';

import Icon from '../../components/Icon';
import IDHelpers from '../../../core/helpers/IDHelper';
import { Container } from './styled';
import { fileTypes } from './constants';

import {
  convertBytesToMegabytes,
  convertFileToBase64,
  convertToNumber,
  downloadBase64File,
  getFileExtension
} from './helpers';

import ComponentFile from './components/file';

import ModalRemoveAll from './modals/all';
import ModalRemoveSingle from './modals/single';
import MemoDescription from './memos/description';
import MemoSize from './memos/size';

type TAcceptedFiles = keyof typeof fileTypes;

interface IProps {
  acceptedFiles: TAcceptedFiles[];
  dataFiles: any[];
  deleteFiles: (files: any[]) => void;
  downloadFiles: (files: any[]) => void;
  emitFiles: (files: any[]) => void;
  idValue: string;
  maxFiles: number;
  maxSizeMegabytes: number;
  nameValue: string;
  pathValue: string;
  sizeValue: string;
  validateDocuments?: (isValid: boolean) => void;
  noDown?: boolean;
  disabled?: boolean;
  customText?: boolean;
}

const FileUpload = ({
  acceptedFiles,
  dataFiles,
  deleteFiles,
  downloadFiles,
  emitFiles,
  idValue,
  maxFiles,
  maxSizeMegabytes,
  nameValue,
  pathValue,
  sizeValue,
  validateDocuments,
  noDown,
  disabled = false,
  customText
}: IProps) => {
  const inputRef = createRef<HTMLInputElement>();

  const [accept, setAccept] = useState<string[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [modalMultiple, setModalMultiple] = useState(false);
  const [modalSingle, setModalSingle] = useState(false);
  const [selected, setSelected] = useState<any[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [maxFilesAndSizeFlag, setMaxFilesAndSizeFlag] = useState(false);
  const [errorFilesExtension, setErrorFilesExtension] = useState(false);
  const [errorFilesNames, setErrorFilesNames] = useState(false);
  const [errorFileSize, setErrorFileSize] = useState(false);

  const controllerRemoveFiles = () => {
    setModalMultiple(true);
    setSelected(files);
    setMaxFilesAndSizeFlag(false);
  };

  const controllerRemoveFile = (file: any) => {
    setModalSingle(true);
    setSelected([file]);
    if (selected.length === 0) {
      setMaxFilesAndSizeFlag(false);
    }
  };

  const controllerRemove = () => {
    const cloud = selected.filter((v) => v.path);
    const local = files.filter(
      (file) => !selected.some((sel) => sel.id === file.id)
    );
    deleteFiles(cloud);
    emitFiles(local);
    setModalMultiple(false);
    setModalSingle(false);
    setSelected([]);
    setFiles([...local, ...cloud]);
  };

  const controllerCancel = () => {
    setModalMultiple(false);
    setModalSingle(false);
    setSelected([]);
  };

  const controllerDownload = (file: any) => {
    if (file.base64) downloadBase64File(file.base64, file[nameValue]);
    else downloadFiles([file]);
  };

  const formatFiles = async (xFiles: File[]) => {
    const arr: any[] = [];
    await Promise.all(
      xFiles.map(async (file: File) => {
        arr.push({
          base64: await convertFileToBase64(file),
          id: String(IDHelpers.generateID()),
          [nameValue]: file.name,
          ext: getFileExtension(file.name),
          path: null,
          [sizeValue]: convertBytesToMegabytes(file.size)
        });
      })
    );
    emitFiles([...dataFiles, ...arr]);
  };

  const onChange = async (e: any) => {
    e.preventDefault();
    const xFiles: File[] = Array.from(e.target.files);
    const allowedSlots = maxFiles - dataFiles.length;
    const filesToAdd = allowedSlots > 0 ? xFiles.slice(0, allowedSlots) : [];
    if (filesToAdd.length > 0) {
      await formatFiles(filesToAdd);
    }
    e.target.value = '';
  };

  const onDragOver = (e: any) => {
    e.preventDefault();
    if (!maxFilesAndSizeFlag) setDragOver(true);
  };

  const onDragLeave = (e: any) => {
    e.preventDefault();
    setDragOver(false);
  };

  const onDragStart = (e: any) => {
    e.preventDefault();
  };

  const onDrop = async (e: any) => {
    e.preventDefault();
    setDragOver(false);
    if (!maxFilesAndSizeFlag) {
      const xFiles: File[] = Array.from(e.dataTransfer.files);
      const allowedSlots = maxFiles - dataFiles.length;
      const filesToAdd = allowedSlots > 0 ? xFiles.slice(0, allowedSlots) : [];
      if (filesToAdd.length > 0) {
        await formatFiles(filesToAdd);
      }
    }
  };

  useEffect(() => {
    if (dataFiles.length > 0) {
      const data = dataFiles.map((v) => ({
        base64: v.base64 ?? null,
        id: v[idValue],
        [nameValue]: v[nameValue],
        ext: getFileExtension(v[nameValue]),
        path: v[pathValue],
        [sizeValue]: convertToNumber(v[sizeValue])
      }));
      setFiles(data);
    }
    return () => {};
  }, [dataFiles]);

  useEffect(() => {
    const arr: string[] = [];
    acceptedFiles.forEach((v1) => {
      Object.entries(fileTypes).forEach(([key, value]) => {
        if (v1 === key) {
          value.forEach((v2) => arr.push(v2));
        }
      });
    });
    setAccept(arr);

    return () => {};
  }, [acceptedFiles]);

  useEffect(() => {
    if (files?.length > 0) {
      const count = [...files].reduce((prev, curr) => prev + curr.size, 0);
      const totalSize = Number(count.toFixed(2));
      setMaxFilesAndSizeFlag(
        dataFiles.length >= maxFiles || totalSize >= maxSizeMegabytes
      );
      if (files?.length > 0 && validateDocuments) {
        validateDocuments(
          totalSize < maxSizeMegabytes &&
            dataFiles.length <= maxFiles &&
            !errorFilesExtension
        );
      }
    }
  }, [errorFilesExtension]);

  // valida extensión de archivos
  useEffect(() => {
    if (files?.length > 0) {
      const maxFileSize = maxSizeMegabytes / maxFiles;
      const arrFaults: boolean[] = [];

      files.forEach((file) => {
        if (!accept.includes(file.ext)) {
          setErrorFilesExtension(true);
          arrFaults.push(true);
        } else if (
          Math.round(file.size * 100) > Math.round(maxFileSize * 100)
        ) {
          setErrorFileSize(true);
          arrFaults.push(true);
        } else if (file.originalName?.length > 200) {
          setErrorFilesNames(true);
          arrFaults.push(true);
        } else {
          setErrorFilesExtension(false);
          setErrorFileSize(false);
          setErrorFilesNames(false);
        }
      });
      setErrorFilesExtension(arrFaults.includes(true));

      const count = [...files].reduce((prev, curr) => prev + curr.size, 0);
      const totalSize = Number(count.toFixed(2));
      setMaxFilesAndSizeFlag(
        dataFiles.length >= maxFiles || totalSize >= maxSizeMegabytes
      );

      if (validateDocuments) {
        validateDocuments(
          dataFiles.length <= maxFiles &&
            !errorFilesExtension &&
            !errorFilesNames &&
            !errorFileSize &&
            !arrFaults.includes(true)
        );
      }
    }
  }, [files]);

  return (
    <Container>
      <input
        ref={inputRef}
        accept={accept.join(', ')}
        multiple
        hidden
        type="file"
        data-testid="cmp-upload-input"
        onChange={onChange}
      />
      <MemoDescription
        maxFiles={maxFiles}
        maxSizeMegabytes={maxSizeMegabytes}
        acceptedFiles={acceptedFiles}
        customText={customText}
      />
      {files?.length > 0 && (
        <MemoSize files={files} maxSizeMegabytes={maxSizeMegabytes} />
      )}
      <div
        className={`file-upload-drag ${
          maxFilesAndSizeFlag || disabled ? 'disable' : ''
        }`}
        draggable
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDragStart={onDragStart}
        onDrop={onDrop}
        data-testid="cmp-upload-drag-and-drop"
      >
        <button
          type="button"
          className={`${disabled ? 'disable' : ''} file-upload-button ${
            maxFilesAndSizeFlag ? 'disable' : ''
          }`}
          data-testid="cmp-upload-button-input"
          onClick={() => {
            if (inputRef.current) inputRef.current.click();
          }}
        >
          {!dragOver ? (
            <>
              <Icon
                name="upload"
                size={19}
                color={!maxFilesAndSizeFlag ? '#5B36F2' : '#8090bb'}
              />
              <span>Subir Archivo</span>
            </>
          ) : (
            <span style={{ color: '#2e0e93' }}>Arrastra tu archivo aquí</span>
          )}
        </button>
      </div>
      {files.length > 0 && (
        <div className="file-upload-actions">
          <span>{files.length} archivo(s)</span>
          <button
            type="button"
            className="file-upload-button"
            data-testid="cmp-upload-button-remove-all"
            onClick={() => controllerRemoveFiles()}
          >
            <span>Eliminar todo</span>
          </button>
        </div>
      )}
      <div className="file-upload-items">
        {files.map((file) => (
          <ComponentFile
            noDown={noDown}
            accept={accept}
            download={(v) => controllerDownload(v)}
            file={file}
            key={IDHelpers.generateID()}
            maxFiles={maxFiles}
            maxSizeMegabytes={maxSizeMegabytes}
            nameValue={nameValue}
            remove={(v) => controllerRemoveFile(v)}
            sizeValue={sizeValue}
          />
        ))}
      </div>
      <ModalRemoveAll
        show={modalMultiple}
        onCancel={() => controllerCancel()}
        onConfirm={() => controllerRemove()}
      />
      <ModalRemoveSingle
        show={modalSingle}
        onCancel={() => controllerCancel()}
        onConfirm={() => controllerRemove()}
      />
    </Container>
  );
};

export default FileUpload;
